/** Shared date/time formatting for the compose view. All times render in the
 *  viewer's local timezone and the active locale, 24-hour, NEVER with seconds. */

import { t, intlLocale } from "./i18n.js";

const DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const WEEKDAY_DATE_TIME_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  ...DATE_TIME_OPTS,
};

/** "21/06/2026, 21:00" — no seconds, local time + active locale. */
export function formatDateTime(d: Date): string {
  return d.toLocaleString(intlLocale(), DATE_TIME_OPTS);
}

/** "Saturday, 21/06/2026, 21:00" — weekday prefix, no seconds, local time. */
export function formatWeekdayDateTime(d: Date): string {
  return d.toLocaleString(intlLocale(), WEEKDAY_DATE_TIME_OPTS);
}

/** "Saturday, 21/06/2026" — weekday + date, NO time. For soft projections
 *  (e.g. the viewer's own future slot) where the hour reads as false precision. */
const WEEKDAY_DATE_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
};
export function formatWeekdayDate(d: Date): string {
  return d.toLocaleString(intlLocale(), WEEKDAY_DATE_OPTS);
}

/** "Friday 20/06, 15:00" — weekday + day/month + time, no year, no seconds,
 *  local time. For the compact group-header "next letter" line. */
const SHORT_WEEKDAY_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};
export function formatShortWeekdayDateTime(d: Date): string {
  return d.toLocaleString(intlLocale(), SHORT_WEEKDAY_OPTS);
}

/** True for the NO_DEADLINE sentinel (year-9999 ISO) used by kickoff letters
 *  that haven't been scheduled yet (founder hasn't written their first message). */
export function isUnscheduled(iso: string): boolean {
  return new Date(iso).getUTCFullYear() >= 9999;
}

/** Recurring schedule in the viewer's LOCAL time, e.g. "Wednesdays at 09:00".
 *  Derived from a concrete slot datetime (NOT the bare UTC weekday+hour, which
 *  can't be localized — the offset may shift the weekday). null/unscheduled →
 *  a fallback string. */
const RECURRING_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "long",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};
export function formatRecurringSchedule(iso: string | null): string {
  if (!iso || isUnscheduled(iso)) return t("sched.recurring_unset");
  const parts = new Intl.DateTimeFormat(intlLocale(), RECURRING_OPTS).formatToParts(
    new Date(iso)
  );
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const hh = parts.find((p) => p.type === "hour")?.value ?? "";
  const mm = parts.find((p) => p.type === "minute")?.value ?? "";
  return t("sched.recurring", { weekday, time: `${hh}:${mm}` });
}

/** Footer status: "Publishes 21/06/2026, 21:00".
 *  null → slot unknown (before finalize); NO_DEADLINE → unscheduled. When it's
 *  not the user's turn, an unscheduled draft publishes when their turn comes. */
export function formatPublishAt(iso: string | null, isMyTurn = true): string {
  if (!iso) return t("sched.publishes_at_slot");
  if (isUnscheduled(iso)) {
    return isMyTurn
      ? t("sched.publishes_after_first")
      : t("sched.publishes_when_turn");
  }
  return t("sched.publishes", { when: formatDateTime(new Date(iso)) });
}

/** Header label: "✨ Your turn — publishes …" on the user's turn, or
 *  "📝 Your draft" when drafting ahead. Falls back to a plain label with no
 *  concrete slot. */
export function formatTurnLabel(iso: string | null, isMyTurn = true): string {
  const base = isMyTurn ? t("compose.label.your_turn") : t("compose.label.your_draft");
  if (!iso || isUnscheduled(iso)) return base;
  return t("sched.label_with_slot", {
    label: base,
    when: formatWeekdayDateTime(new Date(iso)),
  });
}

export function formatWriteBy(iso: string | null): string | null {
  if (!iso || isUnscheduled(iso)) return null;
  return formatWeekdayDateTime(new Date(iso));
}
